import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { FREE_SHIPPING_THRESHOLD, cartKey, useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, count } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[61] flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-5">
              <h2 className="font-heading text-lg font-bold tracking-tight">
                Your Bag {count > 0 && <span className="text-neutral-400">({count})</span>}
              </h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 transition-colors hover:bg-neutral-100"
                aria-label="Close cart"
              >
                <X size={18} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                <div className="grid h-16 w-16 place-items-center rounded-full bg-neutral-100">
                  <ShoppingBag size={24} className="text-neutral-400" />
                </div>
                <p className="font-heading text-lg font-semibold">Your bag is empty</p>
                <p className="text-sm text-neutral-500">Beautifully useful things are waiting for you.</p>
                <button
                  data-testid="cart-continue-shopping"
                  onClick={() => {
                    setOpen(false);
                    navigate("/shop");
                  }}
                  className="mt-2 rounded-full bg-neutral-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-neutral-700"
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <>
                <div className="border-b border-neutral-100 px-6 py-4" data-testid="free-shipping-progress">
                  <p className="mb-2 text-xs font-medium text-neutral-600">
                    {remaining > 0 ? (
                      <>Add {inr(remaining)} more for <span className="font-semibold text-neutral-900">free shipping</span></>
                    ) : (
                      <span className="font-semibold text-neutral-900">You've unlocked free shipping</span>
                    )}
                  </p>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
                    <motion.div
                      className="h-full rounded-full bg-neutral-900"
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4" data-lenis-prevent>
                  {items.map((item) => {
                    const key = cartKey(item);
                    return (
                      <div key={key} className="flex gap-4 border-b border-neutral-100 py-4 last:border-0" data-testid={`cart-item-${item.slug}`}>
                        {item.image && <img src={item.image} alt={item.name} className="h-20 w-16 rounded-lg object-cover img-frame" loading="lazy" />}
                        <div className="flex min-w-0 flex-1 flex-col">
                          <p className="truncate text-sm font-medium text-neutral-900">{item.name}</p>
                          {item.variant_title && <p className="text-xs text-neutral-400">{item.variant_title}</p>}
                          <p className="mt-0.5 text-sm font-semibold">{inr(item.price)}</p>
                          <div className="mt-auto flex items-center justify-between pt-2">
                            <div className="flex items-center rounded-full border border-neutral-200">
                              <button
                                data-testid={`cart-qty-minus-${item.slug}`}
                                onClick={() => setQty(key, item.qty - 1)}
                                className="p-1.5 px-2 transition-colors hover:text-neutral-500"
                                aria-label="Decrease quantity"
                              >
                                <Minus size={13} />
                              </button>
                              <span className="w-6 text-center text-sm font-medium">{item.qty}</span>
                              <button
                                data-testid={`cart-qty-plus-${item.slug}`}
                                onClick={() => setQty(key, Math.min(item.qty + 1, 10))}
                                className="p-1.5 px-2 transition-colors hover:text-neutral-500"
                                aria-label="Increase quantity"
                              >
                                <Plus size={13} />
                              </button>
                            </div>
                            <button
                              data-testid={`cart-remove-${item.slug}`}
                              onClick={() => remove(key)}
                              className="rounded-full p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
                              aria-label="Remove item"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-neutral-100 px-6 py-5">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm text-neutral-500">Subtotal</span>
                    <span className="font-heading text-lg font-bold" data-testid="cart-subtotal">{inr(subtotal)}</span>
                  </div>
                  <p className="mb-4 text-xs text-neutral-400">
                    {subtotal >= FREE_SHIPPING_THRESHOLD ? "Shipping: Free" : `Shipping: ${inr(79)} at checkout`} · Taxes included
                  </p>
                  <button
                    data-testid="checkout-button"
                    onClick={() => {
                      setOpen(false);
                      navigate("/checkout");
                    }}
                    className="group flex w-full items-center justify-center gap-2 rounded-full bg-neutral-900 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-700"
                  >
                    Checkout Securely
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
